import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DragEvent as ReactDragEvent, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Grid2x2,
  Grid3x3,
  Hand,
  Magnet,
  Maximize,
  MousePointer2,
  Plus,
  RectangleHorizontal,
  Redo2,
  Rocket,
  RotateCcw,
  Save,
  Tag,
  TriangleAlert,
  Type,
  Undo2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { MapElementType } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { useConfirm } from '@/components/ui/confirm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select } from '@/components/ui/field';
import { Dialog } from '@/components/ui/dialog';
import { ScopeLock } from '@/components/ui/scope-lock';
import { useRequireScope } from '@/Hooks/useRequireScope';
import { MapCanvas, type CanvasTarget, type View } from './MapCanvas';
import { MapPalette } from './MapPalette';
import { ENTITY_DRAG_TYPE, UnplacedPanel } from './UnplacedPanel';
import { ElementInspector } from './ElementInspector';
import { useSceneHistory } from '../lib/useSceneHistory';
import {
  addElements,
  boundsOf,
  bottomZIndex,
  centerOf,
  createElement,
  createLinkedElement,
  duplicateElements,
  elementFill,
  elementPayload,
  expandSelection,
  type FloorScene,
  type HandleId,
  isLinkable,
  type LinkState,
  linkStateOf,
  mapElements,
  newGroupId,
  newLocalId,
  patchElements,
  placeEntities,
  placedEntityIds,
  type PlaceableEntity,
  type Point,
  type Rect,
  rectsIntersect,
  removeElements,
  resizeFromCorner,
  type Scene,
  type SceneElement,
  sceneFromFloor,
  snapTo,
  topZIndex,
} from '../lib/scene';

interface FloorMapEditorProps {
  floor: FloorScene;
  /**
   * Every table and resource the tenant has, on any floor. The editor needs the
   * whole set, not just this floor's: an entity that is missing altogether is a
   * dangling link, while one that merely lives on another floor is not.
   */
  entities: readonly PlaceableEntity[];
  canPublish: boolean;
  onSaved: (floor: FloorScene) => void;
}

type Tool = 'select' | 'pan';

interface Bag {
  base: Scene;
  latest: Scene;
}

type Gesture =
  | ({ kind: 'move'; start: Point; ids: string[] } & Bag)
  | ({ kind: 'resize'; id: string; handle: Exclude<HandleId, 'rotate'> } & Bag)
  | ({ kind: 'rotate'; id: string; center: Point; startAngle: number; startRotation: number } & Bag)
  | { kind: 'marquee'; start: Point; additive: boolean }
  | { kind: 'pan'; startClient: Point; startView: View };

const GRID_SIZES = [10, 20, 40];
const ZOOM_MIN = 0.3;
const ZOOM_MAX = 5;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const angleTo = (from: Point, to: Point) =>
  (Math.atan2(to.y - from.y, to.x - from.x) * 180) / Math.PI;

function rectBetween(a: Point, b: Point): Rect {
  return {
    x: Math.min(a.x, b.x),
    y: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/**
 * §5.2 map editor. The scene document and the history stack live here; the
 * canvas below is the same renderer the live view uses, so edit and live can
 * never drift apart.
 */
export function FloorMapEditor({
  floor,
  entities,
  canPublish,
  onSaved,
}: FloorMapEditorProps) {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const inScope = useRequireScope();
  const initial = useMemo(() => sceneFromFloor(floor), [floor]);
  const history = useSceneHistory(initial);
  const { scene, preview, commit, reset } = history;

  /**
   * §24.4 — both writes this toolbar makes declare `@RequireScope(FLOOR,'floorId')`:
   * `PUT /floor-map/:floorId/elements` and `POST /floor-map/:floorId/publish`.
   * `GET /floor-map` does not, so a floor-scoped supervisor can open any floor's
   * map read-only and, before this, saw a live Save that answered
   * `Out of scope for FLOOR`. The editing tools stay usable — the drawing is
   * local until it is saved — and only the two calls that leave the browser are
   * withdrawn, with the reason attached.
   */
  const floorOutOfScope = inScope('FLOOR', scene.floorId)
    ? null
    : t('spaces.editor.floorOutOfScope');

  const [selection, setSelection] = useState<string[]>([]);
  const [tool, setTool] = useState<Tool>('select');
  const [view, setView] = useState<View>({ x: 0, y: 0, zoom: 1 });
  const [grid, setGrid] = useState(20);
  const [showGrid, setShowGrid] = useState(true);
  const [snap, setSnap] = useState(true);
  /**
   * The frame's `Show labels` checkbox. Genuinely presentational — `MapCanvas`
   * has no such switch and draws a label whenever one resolves and the element
   * is big enough — so it lives here and never reaches a request body.
   */
  const [showLabels, setShowLabels] = useState(true);
  const [marquee, setMarquee] = useState<Rect | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);
  const gestureRef = useRef<Gesture | null>(null);
  const clipboardRef = useRef<SceneElement[]>([]);

  // A new floor is a new document: history and selection start over.
  useEffect(() => {
    reset(initial);
    setSelection([]);
    setView({ x: 0, y: 0, zoom: 1 });
  }, [initial, reset]);

  const toScene = useCallback((event: { clientX: number; clientY: number }): Point => {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return { x: 0, y: 0 };
    const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(ctm.inverse());
    return { x: point.x, y: point.y };
  }, []);

  // -------------------------------------------------------------------------
  // Scene actions — each is exactly one undo step
  // -------------------------------------------------------------------------

  const unlockedSelection = useMemo(
    () =>
      scene.elements
        .filter((element) => selection.includes(element.id) && !element.locked)
        .map((element) => element.id),
    [scene.elements, selection],
  );

  const viewCenter = useCallback(
    (): Point => ({
      x: view.x + scene.width / view.zoom / 2,
      y: view.y + scene.height / view.zoom / 2,
    }),
    [view, scene.width, scene.height],
  );

  const addElement = useCallback(
    (type: MapElementType) => {
      const element = createElement(type, viewCenter(), scene, grid, snap);
      commit(addElements(scene, [element]));
      setSelection([element.id]);
    },
    [commit, grid, scene, snap, viewCenter],
  );

  const patchSelection = useCallback(
    (patch: Partial<SceneElement>) => commit(patchElements(scene, selection, patch)),
    [commit, scene, selection],
  );

  const deleteSelection = useCallback(() => {
    if (unlockedSelection.length === 0) return;
    commit(removeElements(scene, unlockedSelection));
    setSelection([]);
  }, [commit, scene, unlockedSelection]);

  const duplicateSelection = useCallback(() => {
    if (selection.length === 0) return;
    const result = duplicateElements(scene, selection, snap ? grid : 12);
    commit(result.scene);
    setSelection(result.ids);
  }, [commit, grid, scene, selection, snap]);

  const copySelection = useCallback(() => {
    clipboardRef.current = scene.elements.filter((element) => selection.includes(element.id));
  }, [scene.elements, selection]);

  const pasteClipboard = useCallback(() => {
    const source = clipboardRef.current;
    if (source.length === 0) return;
    const offset = snap ? grid : 12;
    const groupRemap = new Map<string, string>();
    let z = topZIndex(scene);
    // Pasted rows are new, so they take local ids and drop the entity link —
    // two elements may never claim the same table (§38).
    const copies: SceneElement[] = source.map((element) => {
      if (element.groupId && !groupRemap.has(element.groupId)) {
        groupRemap.set(element.groupId, newGroupId());
      }
      z += 1;
      return {
        ...element,
        id: newLocalId(),
        x: element.x + offset,
        y: element.y + offset,
        zIndex: z,
        entityType: null,
        entityId: null,
        groupId: element.groupId ? (groupRemap.get(element.groupId) ?? null) : null,
      };
    });
    commit(addElements(scene, copies));
    setSelection(copies.map((copy) => copy.id));
  }, [commit, grid, scene, snap]);

  const groupSelection = useCallback(() => {
    if (selection.length < 2) return;
    commit(patchElements(scene, selection, { groupId: newGroupId() }));
  }, [commit, scene, selection]);

  const ungroupSelection = useCallback(
    () => commit(patchElements(scene, selection, { groupId: null })),
    [commit, scene, selection],
  );

  const toggleLock = useCallback(() => {
    const chosen = scene.elements.filter((element) => selection.includes(element.id));
    const locked = chosen.every((element) => element.locked);
    commit(patchElements(scene, selection, { locked: !locked }));
  }, [commit, scene, selection]);

  const toggleVisible = useCallback(() => {
    const chosen = scene.elements.filter((element) => selection.includes(element.id));
    const hidden = chosen.every((element) => !element.visible);
    commit(patchElements(scene, selection, { visible: hidden }));
  }, [commit, scene, selection]);

  const bringToFront = useCallback(() => {
    let z = topZIndex(scene);
    commit(
      mapElements(scene, selection, (element) => {
        z += 1;
        return { ...element, zIndex: z };
      }),
    );
  }, [commit, scene, selection]);

  const sendToBack = useCallback(() => {
    let z = bottomZIndex(scene);
    commit(
      mapElements(scene, selection, (element) => {
        z -= 1;
        return { ...element, zIndex: z };
      }),
    );
  }, [commit, scene, selection]);

  const nudge = useCallback(
    (dx: number, dy: number) => {
      if (unlockedSelection.length === 0) return;
      commit(
        mapElements(scene, unlockedSelection, (element) => ({
          ...element,
          x: element.x + dx,
          y: element.y + dy,
        })),
      );
    },
    [commit, scene, unlockedSelection],
  );

  const selectAll = useCallback(
    () => setSelection(scene.elements.filter((element) => element.visible).map((el) => el.id)),
    [scene.elements],
  );

  // -------------------------------------------------------------------------
  // Pointer gestures — preview while moving, one commit when the gesture ends
  // -------------------------------------------------------------------------

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>, target: CanvasTarget) => {
      if (event.button !== 0 && event.button !== 1) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      const point = toScene(event);

      if (tool === 'pan' || event.button === 1) {
        gestureRef.current = {
          kind: 'pan',
          startClient: { x: event.clientX, y: event.clientY },
          startView: view,
        };
        return;
      }

      if (target.kind === 'handle') {
        const element = scene.elements.find((candidate) => candidate.id === target.id);
        if (!element || element.locked) return;
        if (target.handle === 'rotate') {
          const center = centerOf(element);
          gestureRef.current = {
            kind: 'rotate',
            id: element.id,
            center,
            startAngle: angleTo(center, point),
            startRotation: element.rotation,
            base: scene,
            latest: scene,
          };
        } else {
          gestureRef.current = {
            kind: 'resize',
            id: element.id,
            handle: target.handle,
            base: scene,
            latest: scene,
          };
        }
        return;
      }

      if (target.kind === 'element') {
        const element = scene.elements.find((candidate) => candidate.id === target.id);
        if (!element) return;
        // Alt breaks a group open so one member can be edited on its own.
        const withGroup = expandSelection(scene, [element.id], !event.altKey);
        let next: string[];
        if (event.shiftKey) {
          next = selection.includes(element.id)
            ? selection.filter((id) => !withGroup.includes(id))
            : [...new Set([...selection, ...withGroup])];
        } else {
          next = selection.includes(element.id) ? selection : withGroup;
        }
        setSelection(next);

        const draggable = scene.elements
          .filter((candidate) => next.includes(candidate.id) && !candidate.locked)
          .map((candidate) => candidate.id);
        if (draggable.length > 0) {
          gestureRef.current = {
            kind: 'move',
            start: point,
            ids: draggable,
            base: scene,
            latest: scene,
          };
        }
        return;
      }

      if (!event.shiftKey) setSelection([]);
      gestureRef.current = { kind: 'marquee', start: point, additive: event.shiftKey };
      setMarquee({ x: point.x, y: point.y, width: 0, height: 0 });
    },
    [scene, selection, toScene, tool, view],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const gesture = gestureRef.current;
      if (!gesture) return;

      if (gesture.kind === 'pan') {
        const svg = svgRef.current;
        if (!svg) return;
        // Client-space maths: converting through the live viewBox would feed
        // the pan back into itself.
        const rect = svg.getBoundingClientRect();
        const scale = scene.width / gesture.startView.zoom / (rect.width || 1);
        setView({
          ...gesture.startView,
          x: gesture.startView.x - (event.clientX - gesture.startClient.x) * scale,
          y: gesture.startView.y - (event.clientY - gesture.startClient.y) * scale,
        });
        return;
      }

      const point = toScene(event);

      if (gesture.kind === 'marquee') {
        setMarquee(rectBetween(gesture.start, point));
        return;
      }

      if (gesture.kind === 'move') {
        const anchor = gesture.base.elements.find((element) => element.id === gesture.ids[0]);
        if (!anchor) return;
        const dx = snapTo(anchor.x + point.x - gesture.start.x, grid, snap) - anchor.x;
        const dy = snapTo(anchor.y + point.y - gesture.start.y, grid, snap) - anchor.y;
        gesture.latest = mapElements(gesture.base, gesture.ids, (element) => ({
          ...element,
          x: element.x + dx,
          y: element.y + dy,
        }));
        preview(gesture.latest);
        return;
      }

      if (gesture.kind === 'resize') {
        const element = gesture.base.elements.find((candidate) => candidate.id === gesture.id);
        if (!element) return;
        const resized = resizeFromCorner(element, gesture.handle, point, grid, snap);
        gesture.latest = mapElements(gesture.base, [gesture.id], () => resized);
        preview(gesture.latest);
        return;
      }

      const delta = angleTo(gesture.center, point) - gesture.startAngle;
      const raw = gesture.startRotation + delta;
      const rotation = ((snap ? Math.round(raw / 15) * 15 : Math.round(raw)) % 360 + 360) % 360;
      gesture.latest = patchElements(gesture.base, [gesture.id], { rotation });
      preview(gesture.latest);
    },
    [grid, preview, scene.width, snap, toScene],
  );

  const endGesture = useCallback(
    (event: ReactPointerEvent<SVGSVGElement>) => {
      const gesture = gestureRef.current;
      gestureRef.current = null;
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      if (!gesture) return;

      if (gesture.kind === 'marquee') {
        const box = marquee;
        setMarquee(null);
        if (!box || box.width < 2 || box.height < 2) return;
        const hits = scene.elements
          .filter((element) => element.visible && !element.locked)
          .filter((element) => {
            const bounds = boundsOf([element]);
            return bounds ? rectsIntersect(bounds, box) : false;
          })
          .map((element) => element.id);
        const expanded = expandSelection(scene, hits, true);
        setSelection((current) =>
          gesture.additive ? [...new Set([...current, ...expanded])] : expanded,
        );
        return;
      }

      if (gesture.kind === 'pan') return;
      commit(gesture.latest);
    },
    [commit, marquee, scene],
  );

  // Wheel zoom keeps the point under the cursor fixed; the listener is native
  // so it can preventDefault (React's is passive).
  const stateRef = useRef({ view, scene, toScene });
  stateRef.current = { view, scene, toScene };

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const { view: current, scene: document, toScene: project } = stateRef.current;
      const anchor = project(event);
      const zoom = clamp(current.zoom * (event.deltaY < 0 ? 1.12 : 1 / 1.12), ZOOM_MIN, ZOOM_MAX);
      const fx = (anchor.x - current.x) / (document.width / current.zoom);
      const fy = (anchor.y - current.y) / (document.height / current.zoom);
      setView({
        zoom,
        x: anchor.x - fx * (document.width / zoom),
        y: anchor.y - fy * (document.height / zoom),
      });
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
  }, []);

  const zoomBy = useCallback((factor: number) => {
    setView((current) => {
      const { scene: document } = stateRef.current;
      const zoom = clamp(current.zoom * factor, ZOOM_MIN, ZOOM_MAX);
      const cx = current.x + document.width / current.zoom / 2;
      const cy = current.y + document.height / current.zoom / 2;
      return { zoom, x: cx - document.width / zoom / 2, y: cy - document.height / zoom / 2 };
    });
  }, []);

  // -------------------------------------------------------------------------
  // Save and publish
  // -------------------------------------------------------------------------

  const save = useCallback(async () => {
    // ⌘S reaches `save()` without passing the toolbar button, so the scope check
    // belongs on the call, not only on the control.
    if (floorOutOfScope) {
      toast.error(floorOutOfScope);
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put<FloorScene>(`/floor-map/${scene.floorId}/elements`, {
        elements: scene.elements.map(elementPayload),
        deleteMissing: true,
      });
      // The response is authoritative — local ids have become real ones, so the
      // undo stack is rebased onto it rather than left pointing at rows that no
      // longer exist.
      reset(sceneFromFloor(data));
      setSelection([]);
      onSaved(data);
      toast.success(t('spaces.editor.saved'));
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } }).response?.data?.message ??
        t('spaces.editor.saveError');
      toast.error(String(message));
    } finally {
      setSaving(false);
    }
  }, [floorOutOfScope, onSaved, reset, scene.elements, scene.floorId, t]);

  const publish = useCallback(async () => {
    if (floorOutOfScope) {
      toast.error(floorOutOfScope);
      return;
    }
    setPublishing(true);
    try {
      await api.post(`/floor-map/${scene.floorId}/publish`);
      toast.success(t('spaces.editor.published'));
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: { message?: string } } })
        .response;
      const message = String(response?.data?.message ?? t('spaces.editor.publishError'));
      if (response?.status === 409) {
        setConflict(message);
        const affected = scene.elements
          .filter((element) => message.includes(element.id))
          .map((element) => element.id);
        if (affected.length > 0) setSelection(affected);
      } else {
        toast.error(message);
      }
    } finally {
      setPublishing(false);
    }
  }, [floorOutOfScope, scene.elements, scene.floorId, t]);

  /**
   * `Discard changes` — the one affordance frame `64:2432` adds rather than
   * removes. It is local: `reset` puts the history stack back on the document
   * the server last sent, so nothing is written and nothing is undoable
   * afterwards. That is why it asks first.
   */
  const discard = useCallback(async () => {
    if (!history.dirty) return;
    const agreed = await confirm({
      title: t('spaces.editor.discardConfirm'),
      body: t('spaces.editor.discardBody'),
      confirmLabel: t('spaces.editor.discard'),
      tone: 'danger',
    });
    if (!agreed) return;
    reset(initial);
    setSelection([]);
  }, [confirm, history.dirty, initial, reset, t]);

  // -------------------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------------------

  const actionsRef = useRef({
    deleteSelection,
    duplicateSelection,
    copySelection,
    pasteClipboard,
    groupSelection,
    ungroupSelection,
    selectAll,
    nudge,
    save,
    undo: history.undo,
    redo: history.redo,
    cancel: history.cancel,
    grid,
  });
  actionsRef.current = {
    deleteSelection,
    duplicateSelection,
    copySelection,
    pasteClipboard,
    groupSelection,
    ungroupSelection,
    selectAll,
    nudge,
    save,
    undo: history.undo,
    redo: history.redo,
    cancel: history.cancel,
    grid,
  };

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const node = event.target as HTMLElement | null;
      if (
        node &&
        (node.isContentEditable || ['INPUT', 'SELECT', 'TEXTAREA'].includes(node.tagName))
      ) {
        return;
      }
      const actions = actionsRef.current;
      const mod = event.metaKey || event.ctrlKey;

      if (event.key === 'Escape') {
        if (gestureRef.current) {
          gestureRef.current = null;
          setMarquee(null);
          actions.cancel();
        } else {
          setSelection([]);
        }
        return;
      }

      if (mod && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) actions.redo();
        else actions.undo();
        return;
      }
      if (mod && event.key.toLowerCase() === 'y') {
        event.preventDefault();
        actions.redo();
        return;
      }
      if (mod && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        actions.duplicateSelection();
        return;
      }
      if (mod && event.key.toLowerCase() === 'c') {
        actions.copySelection();
        return;
      }
      if (mod && event.key.toLowerCase() === 'v') {
        actions.pasteClipboard();
        return;
      }
      if (mod && event.key.toLowerCase() === 'a') {
        event.preventDefault();
        actions.selectAll();
        return;
      }
      if (mod && event.key.toLowerCase() === 'g') {
        event.preventDefault();
        if (event.shiftKey) actions.ungroupSelection();
        else actions.groupSelection();
        return;
      }
      if (mod && event.key.toLowerCase() === 's') {
        event.preventDefault();
        void actions.save();
        return;
      }
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        actions.deleteSelection();
        return;
      }
      const step = event.shiftKey ? 1 : actions.grid;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        actions.nudge(-step, 0);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        actions.nudge(step, 0);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        actions.nudge(0, -step);
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        actions.nudge(0, step);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // -------------------------------------------------------------------------

  const byEntityId = useMemo(
    () => new Map(entities.map((entity) => [entity.id, entity])),
    [entities],
  );

  const knownEntityIds = useMemo(() => new Set(entities.map((entity) => entity.id)), [entities]);

  const floorEntities = useMemo(
    () => entities.filter((entity) => entity.floorId === scene.floorId),
    [entities, scene.floorId],
  );

  // §38 — the gap the operator actually falls into: a table created in setup has
  // no element, so it is invisible on the map until someone places it.
  const unplaced = useMemo(() => {
    const placed = placedEntityIds(scene);
    return floorEntities.filter((entity) => !placed.has(entity.id));
  }, [floorEntities, scene]);

  const flagFor = useCallback(
    (element: SceneElement) => {
      const state: LinkState | null = linkStateOf(element, knownEntityIds);
      if (state === 'unlinked') {
        return { tone: 'warning' as const, title: t('spaces.editor.unlinkedWarning') };
      }
      if (state === 'dangling') {
        return { tone: 'danger' as const, title: t('spaces.editor.danglingLink') };
      }
      return null;
    },
    [knownEntityIds, t],
  );

  // Placing from the unplaced list is the whole point: the element is linked at
  // the moment it exists, so it can never be a shape nobody attached (§38).
  const placeEntity = useCallback(
    (entity: PlaceableEntity, at?: Point) => {
      const element = createLinkedElement(entity, scene, grid, snap, at);
      commit(addElements(scene, [element]));
      setSelection([element.id]);
    },
    [commit, grid, scene, snap],
  );

  const placeMany = useCallback(
    (chosen: readonly PlaceableEntity[]) => {
      if (chosen.length === 0) return;
      const result = placeEntities(scene, chosen, grid, snap);
      commit(result.scene);
      setSelection(result.ids);
    },
    [commit, grid, scene, snap],
  );

  const onEntityDrop = useCallback(
    (event: ReactDragEvent<HTMLDivElement>) => {
      const id =
        event.dataTransfer.getData(ENTITY_DRAG_TYPE) || event.dataTransfer.getData('text/plain');
      const entity = unplaced.find((candidate) => candidate.id === id);
      if (!entity) return;
      event.preventDefault();
      placeEntity(entity, toScene(event));
    },
    [placeEntity, toScene, unplaced],
  );

  const labelFor = useCallback(
    (element: SceneElement) => {
      if (!showLabels) return '';
      if (element.label) return element.label;
      // A linked element is captioned by the entity, so the map always shows the
      // table number operations uses (§38).
      if (element.entityId) {
        const found = byEntityId.get(element.entityId);
        if (found) return found.label;
      }
      return t(`spaces.element.${element.elementType}`);
    },
    [byEntityId, showLabels, t],
  );

  const unlinked = scene.elements.filter(
    (element) => isLinkable(element.elementType) && !element.entityId,
  ).length;

  /**
   * An element still pointing at a table or resource that has been deleted.
   *
   * Both writes refuse it: `bulkUpsert` runs `assertEntityLink()` over every
   * element in the payload and 404s the whole save, and `publish` collects the
   * same elements and 409s. An element nobody ever linked is fine by contrast —
   * publish only inspects elements that carry both `entityType` and `entityId`.
   */
  const dangling = useMemo(
    () => scene.elements.filter((element) => linkStateOf(element, knownEntityIds) === 'dangling'),
    [knownEntityIds, scene.elements],
  );

  const selectDangling = useCallback(() => setSelection(dangling.map((e) => e.id)), [dangling]);

  /**
   * The real table or resource behind the one selected element, if there is one.
   *
   * Frame `64:2432` heads its right panel **Table Properties** and puts
   * `Table Identifier`, `Seats Capacity` and `Floor Section` in it. Those are
   * `VenueTable` columns, not scene data: they are written by
   * `PATCH /tables/:id` — a different permission (`tables.update`) at a
   * different scope (`TABLE`, not `FLOOR`) — and a PATCH fires immediately,
   * which would break the contract this whole editor rests on, that the drawing
   * is local until Save and every step is undoable. So they are shown, and they
   * are read-only, and the panel says where they are edited.
   *
   * The frame's other two inspector controls are not here at all: `Shape` exists
   * on neither model and the renderer draws one rounded rect (shared with the
   * waiter app), and `reservable` occurs exactly once in the schema — on
   * `Area`, not `VenueTable`. Neither is on `CreateTableDto`, so with
   * `forbidNonWhitelisted` either one would 400 the whole table save and take
   * the identifier, seats and section down with it.
   */
  const linkedEntity = useMemo(() => {
    if (selection.length !== 1) return null;
    const element = scene.elements.find((candidate) => candidate.id === selection[0]);
    if (!element?.entityId) return null;
    return byEntityId.get(element.entityId) ?? null;
  }, [byEntityId, scene.elements, selection]);

  /** The frame's three quick-add tools. The full 13-type palette stays in the aside. */
  const quickAdd = [
    { type: MapElementType.TABLE, glyph: Grid2x2 },
    { type: MapElementType.WALL, glyph: RectangleHorizontal },
    { type: MapElementType.TEXT, glyph: Type },
  ] as const;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_19rem]">
      <div className="min-w-0 space-y-3">
        {/* Frame `64:2432` separates the two: what the map *is* on the left,
            what happens to it on the right. Publish is not on the frame at all —
            it is its own permission (`floor_map.publish`) and the only place in
            the product where that authority can be exercised, so removing it
            would leave the key with nowhere to go. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {unlinked > 0 && (
              <Badge tone="warning" title={t('spaces.editor.unlinkedWarning')}>
                {t('spaces.editor.unlinkedCount', { count: unlinked })}
              </Badge>
            )}
            <Badge tone={history.dirty ? 'warning' : 'success'}>
              {history.dirty ? t('spaces.editor.unsaved') : t('spaces.editor.allSaved')}
            </Badge>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void discard()}
              disabled={!history.dirty}
              title={history.dirty ? undefined : t('spaces.editor.nothingToSave')}
            >
              {/* `RotateCcw` is a reset, not a movement, so it is deliberately
                  not mirrored in Arabic — mirroring it draws a redo. */}
              <Icon as={RotateCcw} />
              {t('spaces.editor.discard')}
            </Button>
            <ScopeLock locked={Boolean(floorOutOfScope)} reason={floorOutOfScope ?? ''}>
              <Button
                size="sm"
                onClick={() => void save()}
                disabled={saving || !history.dirty || dangling.length > 0 || !!floorOutOfScope}
                title={
                  floorOutOfScope ??
                  (dangling.length > 0
                    ? t('spaces.editor.danglingBlocks', { count: dangling.length })
                    : !history.dirty
                      ? t('spaces.editor.nothingToSave')
                      : undefined)
                }
              >
                <Icon as={Save} />
                {saving ? t('spaces.editor.saving') : t('common.save')}
              </Button>
            </ScopeLock>
            {canPublish && (
              <ScopeLock locked={Boolean(floorOutOfScope)} reason={floorOutOfScope ?? ''}>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => void publish()}
                  disabled={publishing || history.dirty || dangling.length > 0 || !!floorOutOfScope}
                  title={
                    floorOutOfScope ??
                    (dangling.length > 0
                      ? t('spaces.editor.danglingBlocks', { count: dangling.length })
                      : history.dirty
                        ? t('spaces.editor.publishBlocked')
                        : undefined)
                  }
                >
                  <Icon as={Rocket} />
                  {publishing ? t('spaces.editor.publishing') : t('spaces.editor.publish')}
                </Button>
              </ScopeLock>
            )}
          </div>
        </div>

        {/* One bar, in the frame's order: what to draw with, what to draw, the
            history pair, then the view switches. Everything the frame leaves
            out — pan, zoom, grid size, the select/pan pair — is still here. */}
        <div className="flex flex-wrap items-center gap-2 rounded-xl border bg-surface p-2">
          <div className="flex overflow-hidden rounded-lg border">
            <button
              type="button"
              onClick={() => setTool('select')}
              aria-pressed={tool === 'select'}
              title={t('spaces.editor.select')}
              className={`px-2.5 py-2 ${tool === 'select' ? 'bg-accent-solid text-accent-on' : 'hover:bg-hover'}`}
            >
              <Icon as={MousePointer2} />
            </button>
            <button
              type="button"
              onClick={() => setTool('pan')}
              aria-pressed={tool === 'pan'}
              title={t('spaces.editor.pan')}
              className={`px-2.5 py-2 ${tool === 'pan' ? 'bg-accent-solid text-accent-on' : 'hover:bg-hover'}`}
            >
              <Icon as={Hand} />
            </button>
          </div>

          <div className="flex flex-wrap gap-1">
            {quickAdd.map(({ type, glyph }) => (
              <Button
                key={type}
                size="sm"
                variant="outline"
                onClick={() => addElement(type)}
              >
                <Icon as={glyph} />
                {t('spaces.editor.addElement', {
                  element: t(`spaces.element.${type}`).toLocaleLowerCase(),
                })}
              </Button>
            ))}
          </div>

          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              onClick={history.undo}
              disabled={!history.canUndo}
              title={`${t('spaces.editor.undo')} (⌘Z)`}
            >
              {/* `Icon` mirrors these two in Arabic. They were bare glyphs here,
                  the only editor in the app that did not turn them around. */}
              <Icon as={Undo2} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={history.redo}
              disabled={!history.canRedo}
              title={`${t('spaces.editor.redo')} (⇧⌘Z)`}
            >
              <Icon as={Redo2} />
            </Button>
          </div>

          <div className="flex gap-1">
            <Button
              size="sm"
              variant={showGrid ? 'secondary' : 'outline'}
              onClick={() => setShowGrid((value) => !value)}
              aria-pressed={showGrid}
              title={t('spaces.editor.grid')}
            >
              <Icon as={Grid3x3} />
            </Button>
            <Button
              size="sm"
              variant={snap ? 'secondary' : 'outline'}
              onClick={() => setSnap((value) => !value)}
              aria-pressed={snap}
              title={t('spaces.editor.snap')}
            >
              <Icon as={Magnet} />
            </Button>
            <Button
              size="sm"
              variant={showLabels ? 'secondary' : 'outline'}
              onClick={() => setShowLabels((value) => !value)}
              aria-pressed={showLabels}
              title={t('spaces.editor.showLabels')}
            >
              <Icon as={Tag} />
            </Button>
            <Select
              className="h-9 w-20"
              value={grid}
              aria-label={t('spaces.editor.gridSize')}
              onChange={(event) => setGrid(Number(event.currentTarget.value))}
            >
              {GRID_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}px
                </option>
              ))}
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => zoomBy(1 / 1.2)} title={t('spaces.editor.zoomOut')}>
              <Icon as={ZoomOut} />
            </Button>
            <span className="w-12 text-center text-xs tabular-nums">
              {Math.round(view.zoom * 100)}%
            </span>
            <Button size="sm" variant="outline" onClick={() => zoomBy(1.2)} title={t('spaces.editor.zoomIn')}>
              <Icon as={ZoomIn} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setView({ x: 0, y: 0, zoom: 1 })}
              title={t('spaces.editor.zoomReset')}
            >
              <Icon as={Maximize} />
            </Button>
          </div>
        </div>

        {/* Neither save nor publish can succeed while an element points at a
            deleted entity, so the blocker is named where the buttons are, with
            the shortest route to the fix. */}
        {dangling.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/50 p-3 text-sm text-red-900 dark:text-red-200">
            <Icon as={TriangleAlert} className="shrink-0 text-red-600 dark:text-red-400" aria-hidden />
            <span className="min-w-0 flex-1">
              {t('spaces.editor.danglingBlocks', { count: dangling.length })}
            </span>
            <Button size="sm" variant="secondary" onClick={selectDangling}>
              {t('spaces.editor.danglingSelect', { count: dangling.length })}
            </Button>
          </div>
        )}

        {/* Impossible to miss: a floor with tables that are not on the map is a
            map that cannot show occupancy, so it is said out loud, not hidden
            behind a palette. */}
        {unplaced.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-900 dark:text-amber-200">
            <Icon as={TriangleAlert} className="shrink-0 text-amber-600" aria-hidden />
            <span className="min-w-0 flex-1">
              {t('spaces.editor.unplaced.banner', { count: unplaced.length })}
            </span>
            <Button size="sm" variant="secondary" onClick={() => placeMany(unplaced)}>
              <Icon as={Plus} />
              {t('spaces.editor.unplaced.placeAll', { count: unplaced.length })}
            </Button>
          </div>
        )}

        <div
          className="scrollbar-thin overflow-hidden rounded-xl border bg-surface p-2"
          onDragOver={(event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={onEntityDrop}
        >
          <MapCanvas
            ref={svgRef}
            scene={scene}
            mode="edit"
            view={view}
            grid={grid}
            showGrid={showGrid}
            selection={selection}
            marquee={marquee}
            fillFor={(element) => elementFill[element.elementType]}
            labelFor={labelFor}
            flagFor={flagFor}
            onCanvasPointerDown={onPointerDown}
            onCanvasPointerMove={onPointerMove}
            onCanvasPointerUp={endGesture}
            ariaLabel={t('spaces.editor.canvasLabel', { floor: scene.name })}
            className={`w-full ${tool === 'pan' ? 'cursor-grab' : ''}`}
          />
        </div>

        <p className="text-xs text-ink-muted">{t('spaces.editor.shortcuts')}</p>
      </div>

      <aside className="space-y-3">
        <Card>
          <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
            <CardTitle className="text-sm">{t('spaces.editor.unplaced.title')}</CardTitle>
            {unplaced.length > 0 && <Badge tone="warning">{unplaced.length}</Badge>}
          </CardHeader>
          <CardContent>
            <UnplacedPanel
              unplaced={unplaced}
              total={floorEntities.length}
              onPlace={(entity) => placeEntity(entity)}
              onPlaceMany={placeMany}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('spaces.editor.palette')}</CardTitle>
          </CardHeader>
          <CardContent>
            <MapPalette onAdd={addElement} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{t('spaces.editor.inspector')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {linkedEntity && (
              <div className="rounded-lg border p-3">
                <p className="text-sm font-medium">
                  {t('spaces.editor.linkedTable')}
                </p>
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                      {t('setup.table.number')}
                    </dt>
                    <dd className="text-sm">{linkedEntity.label}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                      {t('setup.table.seats')}
                    </dt>
                    <dd className="text-sm tabular-nums">{linkedEntity.capacity ?? '—'}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-[11px] uppercase tracking-wide rtl:tracking-normal text-ink-muted">
                      {t('settings.scope.area')}
                    </dt>
                    <dd className="text-sm">{linkedEntity.areaName || '—'}</dd>
                  </div>
                </dl>
                <p className="mt-2 text-xs text-ink-muted">
                  {t('spaces.editor.linkedTableHint')}
                </p>
              </div>
            )}
            <ElementInspector
              scene={scene}
              selection={selection}
              floorEntities={floorEntities}
              knownEntityIds={knownEntityIds}
              onPatch={patchSelection}
              onDuplicate={duplicateSelection}
              onDelete={deleteSelection}
              onGroup={groupSelection}
              onUngroup={ungroupSelection}
              onToggleLock={toggleLock}
              onToggleVisible={toggleVisible}
              onBringToFront={bringToFront}
              onSendToBack={sendToBack}
            />
          </CardContent>
        </Card>
      </aside>

      <Dialog
        open={conflict !== null}
        onClose={() => setConflict(null)}
        title={t('spaces.editor.publishConflict')}
        description={t('spaces.editor.publishConflictHint')}
        footer={
          <Button variant="outline" onClick={() => setConflict(null)}>
            {t('common.close')}
          </Button>
        }
      >
        <p className="flex items-start gap-2 text-sm">
          <Icon as={TriangleAlert} className="mt-0.5 shrink-0 text-amber-500" />
          <span className="break-words">{conflict}</span>
        </p>
      </Dialog>
    </div>
  );
}
